import {
  ArchiveIcon,
  ClockIcon,
  PersonIcon, 
  BookmarkIcon
} from '@radix-ui/react-icons';
import { ApexOptions } from "apexcharts";
import Chart from "react-apexcharts";
import { useGetLibraryStatsQuery } from '../../hooks/useGetLibraryStatsQuery';
import { MockLibraryStats } from '../../services/library/mockLibrayStats';
import {
  Box,
  Card,
  Container,
  Flex,
  Grid,
  Heading,
  Spinner,
  Text
} from '@radix-ui/themes';
import { ErrorState } from '../../components/common/EmptyState';
import { useChartTheme } from '../../hooks/useChartTheme';
import { TopUserType } from '../../types/Library';

export const LibraryStats = () => {
  const { baseOptions, palette } = useChartTheme();
  const { data, status, error, isLoading } =
          // {
          //   data: MockLibraryStats, 
          //   status: true, 
          //   error: false,
          //   isLoading: false
          // } 
   useGetLibraryStatsQuery();

  if (isLoading) return (
    <Flex justify="center" align="center" gap="2" height="100%" p="4">
      <Spinner loading />
      <Text color="gray">A carregar estatísticas...</Text>
    </Flex>
  );

  if (error) return (
    <Box p="4">
      <ErrorState
        title="Erro ao carregar estatísticas"
        description="Não foi possível obter as estatísticas da biblioteca. Por favor, tente novamente."
      />
    </Box>
  );

  const chartOptions: ApexOptions = {
    ...baseOptions,
    colors: palette,
    legend: {
      ...baseOptions.legend,
      position: 'bottom',
      fontFamily: 'inherit'
    },
    plotOptions: {
      pie: {
        donut: {
          labels: {
            show: true,
            total: {
              show: false,
              label: 'Total',
            }
          }
        }
      }
    }
  };

  const StatCard = ({ icon: Icon, title, value }:any) => (
    <Card variant="surface">
      <Flex align="center" gap="4">
        <Flex
          align="center"
          justify="center"
          style={{
            width: 'var(--space-7)',
            height: 'var(--space-7)',
            borderRadius: 'var(--radius-4)',
            backgroundColor: 'var(--accent-3)',
            color: 'var(--accent-11)',
          }}
        >
          <Icon width={24} height={24} />
        </Flex>
        <Box>
          <Text as="p" size="2" color="gray" weight="medium">{title}</Text>
          <Text as="p" size="4" weight="bold">{value}</Text>
        </Box>
      </Flex>
    </Card>
  );

  return (
    <Box p="4">
      <Container>
        <Heading 
          align="center" 
          size="8" 
          weight="bold" 
          color="gray" 
          mb="6"
        >
          {/* Painel de Estatísticas da Biblioteca */}
        </Heading>

        {/* Quick Stats Grid */}
        <Grid columns={{ initial: "1", md: "3" }} gap="4" mb="6">
          <StatCard 
            icon={BookmarkIcon} 
            title="Livros Totais" 
            value={data?.totalBooks} 
          />
          <StatCard 
            icon={ClockIcon} 
            title="Reservas Ativas" 
            value={data?.activeReservations} 
          />
          <StatCard 
            icon={ArchiveIcon} 
            title="Empréstimos Ativos" 
            value={data?.activeLoansCount} 
          />
          {/* <StatCard 
            icon={PersonIcon} 
            title="Média de Empréstimo" 
            value={`${data?.averageLoanDuration?.toFixed(1)??0} dias`} 
          /> */}
        </Grid>

        {/* Charts Section */}
        <Grid columns={{ initial: "1", md: "2" }} gap="4">
          {/* Donut Charts */}
          <Card variant="surface">
            <Heading size="4" mb="4">Livros</Heading>
            <Chart 
              options={{
                ...chartOptions,
                labels: ["Emprestados", "Disponíveis"],
                title: { text: "Percentagem de Livros" }
              }}
              series={[
                data?.loanedBooksPercentage || 0, 
                100 - (data?.loanedBooksPercentage || 0)
              ]}
              type="donut"
              height={300}
            />
          </Card>

          <Card variant="surface">
            <Heading size="4" mb="4">Devoluções</Heading>
            <Chart 
              options={{
                ...chartOptions,
                labels: ["Atrasados", "Pontuais"],
                title: { text: "Taxa de Devolução" }
              }}
              series={[
                data?.lateReturnRate || 0, 
                100 - (data?.lateReturnRate || 0)
              ]}
              type="donut"
              height={300}
            />
          </Card>

          {/* Bar Charts */}
          <Card variant="surface" style={{ gridColumn: '1 / -1' }}>
            <Heading size="4" mb="4">Livros mais requisitados do mês</Heading>
            <Chart 
              options={{
                ...chartOptions,
                xaxis: {
                  categories: (data?.mostPopularBooksThisMonth || [])
                    .map((b: any) => b.book || "N/D")
                },
                plotOptions: {
                  bar: {
                    borderRadius: 4,
                    horizontal: false,
                  }
                }
              }}
              series={[{
                name: "Reservas",
                data: (data?.mostPopularBooksThisMonth || [])
                  .map((b: any) => b.reservationsCount || 0)
              }]}
              type="bar"
              height={350}
            />
          </Card>
        </Grid>

        {/* Top Users Section */}
        <Card variant="surface" mt="4">
          <Heading size="4" mb="4">Utilizadores mais activos</Heading>
          <Grid columns={{ initial: "1", md: "3" }} gap="4">
            {data?.topUsers?.map((user: TopUserType, index) => (
              <Flex
                key={`Flex${user.user}${index}`}
                align="center"
                gap="3"
                p="4"
                style={{
                  backgroundColor: 'var(--accent-2)',
                  borderRadius: 'var(--radius-3)',
                }}
              >
                <PersonIcon width={24} height={24} style={{ color: 'var(--accent-11)' }} key={`PersonIcon${user.user}${index}`}  />
                <Box key={`Box${user.user}${index}`}>
                  <Text as="p" weight="medium" key={`User${user.user}${index}`}>{user?.user}</Text>
                  <Text as="p" color="gray" size="2" key={`LoansCount${user.user}${index}`}>
                    {user.loansCount} empréstimos
                  </Text>
                </Box>
              </Flex>
            ))}
          </Grid>
        </Card>
      </Container>
    </Box>
  );
};

export default LibraryStats;